// Tab long-press PTT orchestration for ActivityFloatingDock (PTT-4).
// Opens dock → expands Ask → streams transcript → submits on release.

import { useCallback, useRef, type Dispatch, type RefObject, type SetStateAction } from "react";
import type { ActivityDockAskFireflyHandle } from "@/components/activity/firefly/useActivityDockAskFireflyController";
import { useFireflyPttMic } from "@/components/activity/firefly/useFireflyPttMic";
import type { ActivityDockAssistLevel } from "@/lib/activityDock/assistLevel";
import type { FireflyPttTrigger } from "@/lib/firefly/tourTypes";
import { useActivityDockPttGesture } from "./useActivityDockPttGesture";
import { useActivityDockTapVsDrag, type ActivityDockPointerHandlers } from "./useActivityDockTapVsDrag";

export type UseActivityDockFireflyPttArgs = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  assistLevel: ActivityDockAssistLevel;
  tourActive: boolean;
  beginDrag: (clientX: number, clientY: number) => void;
  updateDrag: (clientX: number, clientY: number) => void;
  endDrag: (clientX: number, clientY: number, viewportW: number, viewportH: number) => void;
  cancelDrag: () => void;
};

export type UseActivityDockFireflyPttResult = {
  askRef: RefObject<ActivityDockAskFireflyHandle | null>;
  pttListening: boolean;
  tabPttListening: boolean;
  pointerHandlers: ActivityDockPointerHandlers;
  onPttStartLongPress: () => void;
  onPttStartHotkey: () => void;
  onPttStartDoubleClick: () => void;
  onPttEnd: () => void;
};

export function useActivityDockFireflyPtt({
  open,
  setOpen,
  assistLevel,
  tourActive,
  beginDrag,
  updateDrag,
  endDrag,
  cancelDrag,
}: UseActivityDockFireflyPttArgs): UseActivityDockFireflyPttResult {
  const askRef = useRef<ActivityDockAskFireflyHandle>(null);
  const suppressPttSubmitRef = useRef(false);
  const pendingPttTriggerRef = useRef<FireflyPttTrigger>("long_press");
  const pttEnabled = assistLevel !== "off" && !tourActive;

  const { listening: tabPttListening, start: startTabMic, stop: stopTabMic } = useFireflyPttMic({
    onAppend: (chunk) => askRef.current?.appendPttChunk(chunk),
    onStop: (reason) => {
      if (suppressPttSubmitRef.current) {
        suppressPttSubmitRef.current = false;
        return;
      }
      if (reason === "release" || reason === "max_duration") {
        askRef.current?.submitQuery({
          inputModality: "voice",
          pttTrigger: pendingPttTriggerRef.current,
        });
      }
    },
  });

  const startTabPtt = useCallback(
    (trigger: FireflyPttTrigger) => {
      if (!pttEnabled) return;
      pendingPttTriggerRef.current = trigger;
      setOpen(true);
      window.setTimeout(() => {
        askRef.current?.expandForPtt();
        void startTabMic();
      }, 0);
    },
    [pttEnabled, setOpen, startTabMic],
  );

  const onPttStartLongPress = useCallback(
    () => startTabPtt("long_press"),
    [startTabPtt],
  );
  const onPttStartHotkey = useCallback(() => startTabPtt("hotkey"), [startTabPtt]);
  const onPttStartDoubleClick = useCallback(
    () => startTabPtt("double_click"),
    [startTabPtt],
  );

  const endTabPtt = useCallback(() => {
    void stopTabMic();
  }, [stopTabMic]);

  const cancelTabPtt = useCallback(() => {
    suppressPttSubmitRef.current = true;
    void stopTabMic();
  }, [stopTabMic]);

  const pttBridge = useActivityDockPttGesture({
    enabled: pttEnabled,
    onPttStart: onPttStartLongPress,
    onPttEnd: endTabPtt,
    onPttCancel: cancelTabPtt,
  });

  const pointerHandlers = useActivityDockTapVsDrag({
    open,
    setOpen,
    beginDrag,
    updateDrag,
    endDrag,
    cancelDrag,
    pttBridge,
    onDoubleClickPtt: pttEnabled ? onPttStartDoubleClick : undefined,
  });

  return {
    askRef,
    pttListening: tabPttListening,
    tabPttListening,
    pointerHandlers,
    onPttStartLongPress,
    onPttStartHotkey,
    onPttStartDoubleClick,
    onPttEnd: endTabPtt,
  };
}
